/**
 * useSeoHead — keeps <head> correct across CLIENT-SIDE navigation.
 *
 * First paint is already correct: the edge serves a prerendered shell per world
 * (vite-plugins/worldsSeoPlugin.ts). But a SPA route change fetches no HTML, so without
 * this hook a user who lands on /worlds and clicks through to /vienna keeps the
 * hub's canonical. Non-planet routes are deliberately left alone (spec §8).
 *
 * All decisions live in worldsSeo.seoTags(), which is pure and unit-tested. This
 * file is only the DOM adapter — the project has no jsdom (components are tested
 * through renderToStaticMarkup), so keeping this layer trivial is deliberate.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { brandForHost, seoTags, type SeoTag } from './worldsSeo.ts';

function applyTag(tag: SeoTag): void {
  if (tag.kind === 'title') {
    document.title = tag.text;
    return;
  }

  if (tag.kind === 'link') {
    let link = document.head.querySelector<HTMLLinkElement>(tag.selector);
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', tag.rel);
      document.head.appendChild(link);
    }
    link.setAttribute('href', tag.href);
    return;
  }

  let meta = document.head.querySelector<HTMLMetaElement>(tag.selector);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(tag.attr, tag.key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', tag.content);
}

export function applySeoHead(pathname: string): void {
  if (typeof document === 'undefined') return;
  // Same brand the edge picked for the shell (ADR-0274), read from the live host —
  // otherwise a client-side hop on jubuddy.com would overwrite the jubuddy shell's
  // title with the gghere default, re-introducing the bug one navigation later.
  const brand = brandForHost(typeof window === 'undefined' ? '' : window.location.hostname);
  const tags = seoTags(pathname, brand);
  if (!tags) return;
  for (const tag of tags) applyTag(tag);
}

export function useSeoHead(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    applySeoHead(pathname);
  }, [pathname]);
}
